// ---------------------------------------------------------------------------
// Limited-time offer — helpers only, NO data.
// ---------------------------------------------------------------------------
// The offer window (title, start, end) is the Shopify automatic discount's own
// dates. The server reads them through the Admin API and returns them with the
// serviceability check, for online-sales visitors only:
//
//   { ..., limited_offer: { title, starts_at, ends_at } }   (ISO 8601 strings)
//
// Per-product percentages are the `custom.limited_time_offer_percent` product
// metafield, read via the Storefront API. The Shopify Function reads that same
// metafield, and Shopify only runs it inside the discount's dates, so the price
// shown here and the price charged at checkout can't drift.
//
// Stacking: the percent comes off the volume-tier price, never the list price.

// Normalize the server payload -> { title, startsAt, endsAt } (ms) | null.
export const parseOffer = (raw) => {
    if(!raw || !raw.ends_at) {
        return null;
    }
    const endsAt = Date.parse(raw.ends_at);
    const startsAt = raw.starts_at ? Date.parse(raw.starts_at) : 0;
    if(Number.isNaN(endsAt) || Number.isNaN(startsAt)) {
        return null;
    }
    return { title: raw.title || 'Limited-time offer', startsAt, endsAt };
};

export const isOfferLive = (offer, now = Date.now()) => {
    return !!offer && now >= offer.startsAt && now < offer.endsAt;
};

// Percent off for this product while an offer is live (pass the live offer from
// useServiceability(), which is null outside the window), else 0.
export const offerPercent = (product, offer) => {
    return (offer && product && product.offerPercent > 0) ? product.offerPercent : 0;
};

// `amount` with `percent` off, rounded exactly the way the Shopify Function
// rounds its discount (to the cent, on the amount being discounted).
export const applyOffer = (amount, percent) => {
    const num = parseFloat(amount) || 0;
    if(!(percent > 0)) {
        return num;
    }
    return Math.round((num - Math.round(num * percent) / 100) * 100) / 100;
};

// What to show for a single unit: the price, what to strike through beside it,
// and the percent (0 when no offer applies to this product).
export const offerPricing = (product, offer) => {
    const percent = offerPercent(product, offer);
    const list = parseFloat(product?.price) || 0;
    if(!percent) {
        return { percent: 0, price: list, was: product?.compareAtPrice || null };
    }
    const compareAt = parseFloat(product?.compareAtPrice) || 0;
    return { percent, price: applyOffer(list, percent), was: Math.max(list, compareAt) };
};

// "2d 04:12:09" / "04:12:09" until `endsAt`.
export const formatCountdown = (endsAt, now = Date.now()) => {
    const total = Math.max(0, Math.floor((endsAt - now) / 1000));
    const days = Math.floor(total / 86400);
    const pad = (n) => String(n).padStart(2, '0');
    const clock = `${pad(Math.floor((total % 86400) / 3600))}:${pad(Math.floor((total % 3600) / 60))}:${pad(total % 60)}`;
    return days > 0 ? `${days}d ${clock}` : clock;
};
